import {
  ContentEditorClickableElement,
  ElementSelectorPropsData,
  contentEndReason,
  contentStartReason,
  ContentEditorRoot,
} from '@usertour/types';
import { LauncherStore } from '@/types/store';
import { UsertourComponent, CustomStoreDataContext } from '@/core/usertour-component';
import { logger } from '@/utils';
import { isVisibleNode } from '@usertour/dom';
import { SDKClientEvents, WidgetZIndex } from '@usertour/constants';
import { UsertourElementWatcher } from './usertour-element-watcher';
import { CommonActionHandler, LauncherActionHandler } from '@/core/action-handlers';

const log = logger.scope('launcher');
// Launcher timeout: 1 hour (3600 seconds)
// Longer timeout for launcher since multiple launchers can coexist without affecting each other
const LAUNCHER_TARGET_MISSING_SECONDS = 3600;

export class UsertourLauncher extends UsertourComponent<LauncherStore> {
  // === Properties ===
  private watcher: UsertourElementWatcher | null = null;
  // Serialized target the live watcher was built for (idempotency key).
  private watcherTargetKey: string | null = null;

  // === Abstract Methods Implementation ===
  /**
   * Initialize action handlers for launcher
   */
  protected initializeActionHandlers(): void {
    this.registerActionHandlers([new CommonActionHandler(), new LauncherActionHandler()]);
  }

  /**
   * Checks the launcher
   */
  async check(): Promise<void> {
    try {
      await this.checkTargetVisibility();
      await this.checkAndUpdateThemeSettings();
    } catch (error) {
      log.error('Failed to check the launcher state', error);
    }
  }

  // === Public API Methods ===
  /**
   * Shows the launcher by initializing its store data with closed state.
   * This method sets up the initial state of the launcher without displaying it.
   */
  async show() {
    const storeData = await this.buildStoreData();
    log.debug(
      `Launcher data is ${storeData?.launcherData ? 'present' : 'missing'} (session ${this.getSessionId() || 'none'})`,
    );
    if (!storeData?.launcherData) {
      return;
    }
    const store = {
      ...storeData,
      triggerRef: undefined,
    } as LauncherStore;
    this.setupElementWatcher(store);
  }

  /**
   * Handles the activation of the launcher
   * This method reports the activation event
   */
  async handleActivate() {
    await this.reportActivateEvent();
  }

  /**
   * Handles the tooltip close event
   * This method checks if auto-dismiss is configured and closes the launcher after a delay
   */
  async onTooltipClose() {
    const storeData = this.getStoreData();
    if (!storeData) {
      return;
    }
    const launcherData = storeData.launcherData;
    const tooltip = launcherData?.tooltip;
    // Auto-dismiss after tooltip is closed if configured
    if (tooltip?.settings?.dismissAfterFirstActivation) {
      await this.close(contentEndReason.LAUNCHER_DEACTIVATED);
    }
  }

  /**
   * Handles click events on content editor elements.
   * Processes button clicks and executes associated actions.
   *
   * @param element - The clicked element with its type and data
   */
  async handleOnClick(element: ContentEditorClickableElement) {
    const { type, data } = element;
    if (type === 'button' && data.actions) {
      await this.handleActions(data.actions);
    }
  }

  // === Store Management ===
  /**
   * Gets the z-index for the launcher component
   * @protected
   */
  protected getZIndex(): number {
    const launcherData = this.getLauncherData();
    if (launcherData?.zIndex != null) {
      return launcherData.zIndex;
    }
    return this.getBaseZIndex() + WidgetZIndex.LAUNCHER_OFFSET;
  }

  /**
   * Gets custom launcher store data
   * @param _context - Context object (unused in launcher)
   * @protected
   */
  protected async getCustomStoreData(
    _context: CustomStoreDataContext,
  ): Promise<Partial<LauncherStore>> {
    const launcherData = this.getLauncherData();
    const evaluatedContent = launcherData?.tooltip?.content
      ? await this.evaluateButtonConditionsInData(
          launcherData.tooltip.content as ContentEditorRoot[],
        )
      : launcherData?.tooltip?.content;
    const evaluatedLauncherData = launcherData
      ? {
          ...launcherData,
          tooltip: {
            ...launcherData.tooltip,
            content: evaluatedContent,
          },
        }
      : launcherData;
    return {
      launcherData: evaluatedLauncherData,
    };
  }

  // === Element Watcher ===
  /**
   * Sets up the element watcher for a launcher
   * @private
   */
  /**
   * The store the NEXT element-found should render. The found handler must
   * not rely on its closure: with watcher reuse, a content update that lands
   * while the element is still unfound re-enters here (same targetKey, no
   * element yet) and would otherwise be shown with the OLD closure snapshot
   * when the element finally appears.
   */
  private pendingWatcherStore: LauncherStore | null = null;

  private setupElementWatcher(store: LauncherStore): void {
    this.pendingWatcherStore = store;
    const data = store.launcherData;

    const targetElement = data?.target?.element as ElementSelectorPropsData;
    if (!targetElement) {
      log.warn('Launcher has no target element configured; the launcher is not shown', { data });
      return;
    }

    // Idempotent for an unchanged target: show() re-runs whenever the server
    // re-sends AddLauncher (which it does whenever an ack times out — with many
    // launchers activating at once the SDK's serialized message handling easily
    // exceeds the 2s ack window, so re-sends arrive in waves). Tearing down a
    // LIVE watcher on every re-send wipes its found/announced element state
    // mid-flight and drops most beacons on first paint; keep it instead.
    const targetKey = JSON.stringify(targetElement);
    log.debug(
      `Attaching the launcher: watcher ${
        this.watcher && this.watcherTargetKey === targetKey
          ? 'reused'
          : this.watcher
            ? 'replaced'
            : 'created'
      }, element ${this.watcher?.getElement() ? 'found' : 'not found yet'} (session ${this.getSessionId() || 'none'})`,
    );
    if (this.watcher && this.watcherTargetKey === targetKey) {
      // The re-run may carry NEW session state: a sessionless pre-activation
      // found its element and called startContent (no store written — the
      // ELEMENT_FOUND handler only opens when a session exists), and this
      // re-send is the server handing back the real session. The watcher's
      // once(ELEMENT_FOUND) is already consumed, so complete the found path
      // here or the beacon never opens.
      const el = this.watcher.getElement();
      if (el && this.getSessionId()) {
        if (el.isConnected) {
          // Same gate as the banner's reuse branch: a re-send while the
          // target is CSS-hidden must not force one wrong tick of an open
          // beacon at a hidden element's position — the check loop reopens
          // when the target shows.
          this.setStoreData({
            ...store,
            openState: isVisibleNode(el),
            triggerRef: el as HTMLElement,
          });
          log.debug('Target element handed off to the beacon');
        } else {
          // The found element has since been DETACHED (SPA re-render kept the
          // JS reference but unmounted the node). Don't hand a dead reference
          // to the positioning layer — write the store closed instead; the
          // 200ms check loop's checkVisibility() re-finds by selector and
          // reopens via ELEMENT_CHANGED. (The store must exist either way, or
          // checkTargetVisibility bails on !store and the beacon never opens.)
          this.setStoreData({ ...store, openState: false, triggerRef: undefined });
        }
      }
      return;
    }

    // Clean up existing watcher (target actually changed)
    if (this.watcher) {
      this.watcher.destroy();
      this.watcher = null;
    }

    this.watcherTargetKey = targetKey;
    this.watcher = new UsertourElementWatcher(targetElement);

    // Use launcher-specific timeout (1 hour) for continuous element monitoring
    this.watcher.setTargetMissingSeconds(LAUNCHER_TARGET_MISSING_SECONDS);

    // Handle element found
    this.watcher.once(SDKClientEvents.ELEMENT_FOUND, (el) => {
      if (el instanceof Element) {
        this.handleElementFound(el, this.pendingWatcherStore ?? store);
      }
    });

    // Handle element not found
    this.watcher.once(SDKClientEvents.ELEMENT_FOUND_TIMEOUT, () => {
      this.handleElementNotFound();
    });

    // Handle element changed
    this.watcher.on(SDKClientEvents.ELEMENT_CHANGED, (el) => {
      if (el instanceof Element) {
        this.handleElementChanged(el);
      }
    });
    // Start watching
    this.watcher.findElement();
  }

  /**
   * Handles the element found event
   * @param el - The element that was found
   * @param store - The store data
   */
  private handleElementFound(el: Element, store: LauncherStore): void {
    const sessionId = this.getSessionId();
    log.debug(`Target element found (session ${sessionId || 'none'})`);
    if (!sessionId) {
      this.instance.startContent(
        this.getContentId(),
        contentStartReason.START_FROM_CONDITION,
        undefined,
        false,
      );
    } else {
      this.setStoreData({ ...store, openState: true, triggerRef: el as HTMLElement });
    }
  }

  /**
   * Handles the element not found event
   */
  private handleElementNotFound() {
    this.hide();
  }

  /**
   * Handles the element changed event
   * @param el - The element that was changed
   */
  private handleElementChanged(el: Element): void {
    this.updateStore({
      triggerRef: el,
    });
  }

  // === Visibility Checking ===
  /**
   * Checks and updates the visibility of the target element
   * @private
   */
  private async checkTargetVisibility(): Promise<void> {
    const store = this.getStoreData();
    if (!store || !this.watcher) {
      return;
    }

    const { openState } = store;
    const { isHidden } = await this.watcher.checkVisibility();

    if (!isHidden) {
      if (!openState) {
        this.open();
      }
      return;
    }

    this.hide();
  }

  // === Event Reporting ===
  /**
   * Reports when the launcher is activated by the user
   * Deletes the current tracking session after activation
   */
  private async reportActivateEvent() {
    await this.socketService.activateLauncher({
      sessionId: this.getSessionId(),
    });
  }

  /**
   * Reports when the launcher is dismissed by the user
   * Deletes the current tracking session after dismissal
   * @param reason - The reason for dismissing the launcher
   */
  protected async reportDismissEvent(
    reason: contentEndReason = contentEndReason.LAUNCHER_DEACTIVATED,
  ): Promise<void> {
    await this.socketService.dismissLauncher({
      sessionId: this.getSessionId(),
      endReason: reason,
    });
  }

  // === Lifecycle Hooks ===
  /**
   * Launcher-specific close logic
   * Uses reportDismissEvent instead of endContent for launcher
   * @param reason - The reason for closing the launcher
   * @protected
   */
  protected async onClose(reason: contentEndReason = contentEndReason.USER_CLOSED): Promise<void> {
    await this.reportDismissEvent(reason);
  }

  /**
   * Launcher-specific cleanup logic
   * @protected
   */
  protected onDestroy(): void {
    // Clean up watcher
    if (this.watcher) {
      this.watcher.destroy();
      this.watcher = null;
    }
    this.watcherTargetKey = null;
  }
}
